/**
 * Emotion control for Cartesia TTS — the model emits inline `[emotion]` tags before a sentence
 * (para-chat avatar pattern); we normalize them to Cartesia's supported set and rewrite them as
 * `<emotion value="…"/>` in the spoken transcript. sonic-3 stitches the prosody server-side, so a
 * tag simply rides inline with the words it precedes; `forSpeech()` leaves it intact.
 *
 * Two surfaces:
 *  • renderEmotions() — stateless, for a COMPLETE string (worker utterance echo).
 *  • EmotionStream    — stateful, for the live delta stream (buffers a partial `[…` across chunk
 *    boundaries and hugs a pending tag onto the NEXT prose so the tag never goes out word-less —
 *    a tag-only transcript would produce no audio and stall the TTS drain).
 */
import { forComponent } from '../logging';

const log = forComponent('Emotion');

/** Cartesia sonic-3 emotion vocabulary (primary + extended). Source of truth for normalization
 *  AND for the tag list taught to the model — keep the prompt and the parser in lockstep. */
export const EMOTIONS = [
  // primary (best results)
  'neutral', 'angry', 'excited', 'content', 'sad', 'scared',
  // extended
  'happy', 'enthusiastic', 'elated', 'triumphant', 'amazed', 'surprised', 'flirtatious',
  'curious', 'calm', 'grateful', 'affectionate', 'sympathetic', 'mysterious', 'frustrated',
  'disgusted', 'sarcastic', 'disappointed', 'hurt', 'guilty', 'bored', 'tired', 'nostalgic',
  'apologetic', 'hesitant', 'confused', 'anxious', 'panicked', 'proud', 'confident', 'skeptical',
  'contemplative', 'determined',
  // remaining sonic-3 vocabulary — without these a valid model tag would be DROPPED (flat delivery).
  'joking', 'trust', 'anticipation', 'outraged', 'threatened', 'contempt', 'envious',
  'dejected', 'rejected', 'wistful', 'insecure', 'resigned', 'distant',
] as const;

const VALID = new Set<string>(EMOTIONS);

/** Friendly synonyms → a canonical Cartesia emotion, so the model isn't tripped by exact spelling. */
const ALIASES: Record<string, string> = {
  cheerful: 'happy', joyful: 'happy', joy: 'happy', glad: 'happy', pleased: 'happy', warm: 'affectionate',
  thrilled: 'excited', eager: 'enthusiastic', ecstatic: 'elated', euphoric: 'elated',
  mad: 'angry', furious: 'angry', annoyed: 'frustrated', irritated: 'frustrated', agitated: 'frustrated',
  shocked: 'surprised', astonished: 'amazed', wonder: 'amazed',
  worried: 'anxious', nervous: 'anxious', afraid: 'scared', alarmed: 'panicked',
  unsure: 'hesitant', uncertain: 'hesitant', doubtful: 'skeptical', suspicious: 'skeptical',
  thoughtful: 'contemplative', focused: 'determined', serious: 'determined',
  playful: 'flirtatious', teasing: 'flirtatious', ironic: 'sarcastic', cheeky: 'sarcastic',
  thankful: 'grateful', sorry: 'apologetic', down: 'sad', melancholic: 'sad', gloomy: 'sad',
  peaceful: 'calm', serene: 'calm', relaxed: 'calm', sleepy: 'tired',
  comedic: 'joking', // Cartesia lists "joking/comedic" as one — canonicalize to joking
};

/** Non-verbal cues Cartesia takes verbatim as bracket tags — pass through, never eaten as an emotion. */
const NONVERBAL: Record<string, string> = { laughter: 'laughter', laughs: 'laughter', laugh: 'laughter', laughing: 'laughter' };

/** Normalize a raw tag word to a canonical Cartesia emotion, or null if unmappable. */
export function normalizeEmotion(raw: string): string | null {
  const k = raw.trim().toLowerCase();
  if (VALID.has(k)) return k;
  return ALIASES[k] ?? null;
}

/** Resolve a `[…]` inner word to what should be spoken (`emotion`/`laughter`) — or null to drop it. */
function resolveTag(raw: string): { kind: 'emotion'; value: string } | { kind: 'nonverbal'; value: string } | null {
  const k = raw.trim().toLowerCase();
  if (NONVERBAL[k]) return { kind: 'nonverbal', value: NONVERBAL[k] };
  const e = normalizeEmotion(k);
  return e ? { kind: 'emotion', value: e } : null;
}

/** A `[word]` (or `[two words]`) bracket tag — letters/spaces only, so array indices / code never match. */
const TAG_RE = /\[([a-zA-Z][a-zA-Z ]{0,24})\]/g;
/** A trailing run that could still GROW into a tag (`[exc`) — held back until the next chunk. */
const PARTIAL_RE = /\[[a-zA-Z ]*$/;

export interface EmotionRender {
  /** transcript for TTS — recognized tags rewritten as Cartesia inline tags */
  speech: string;
  /** on-screen echo — original `[tag]` kept when `show`, stripped otherwise */
  display: string;
  /** spoken words only, all tags removed (echo-discrimination + interrupted-reply text) */
  prose: string;
}

const cartesiaTag = (t: { kind: string; value: string }): string =>
  t.kind === 'nonverbal' ? `[${t.value}]` : `<emotion value="${t.value}"/>`;

/** Rewrite a COMPLETE string's `[tag]`s. Stateless — safe only when no tag straddles the input edge
 *  (true for whole worker utterances). For the streaming delta path use EmotionStream. */
export function renderEmotions(text: string, opts: { show: boolean } = { show: true }): EmotionRender {
  let speech = '', display = '', prose = '', last = 0;
  TAG_RE.lastIndex = 0;
  for (let m = TAG_RE.exec(text); m; m = TAG_RE.exec(text)) {
    const before = text.slice(last, m.index);
    speech += before; display += before; prose += before;
    const tag = resolveTag(m[1]);
    if (tag) {
      speech += cartesiaTag(tag);
      if (opts.show) display += m[0];
    } else {
      log.debug(`dropping unknown emotion tag ${m[0]}`);
    }
    last = m.index + m[0].length;
  }
  const tail = text.slice(last);
  return { speech: speech + tail, display: display + tail, prose: prose + tail };
}

/**
 * Streaming emotion parser. `feed(delta)` returns the spans completed by this chunk; a recognized tag
 * is stashed and PREPENDED to the next non-empty prose (Cartesia wants the tag right before its words,
 * and a tag-only continuation would synthesize nothing → drained-but-silent stall). A trailing `[…`
 * that is still a viable tag prefix is buffered until it completes or the stream flushes.
 */
export class EmotionStream {
  private buf = '';
  private pending: { kind: string; value: string } | null = null;
  constructor(public show = true) {}

  feed(delta: string): EmotionRender { this.buf += delta; return this.drain(false); }
  flush(): EmotionRender { return this.drain(true); }

  private drain(final: boolean): EmotionRender {
    let body = this.buf;
    // Hold back a trailing run that could still grow into a tag — unless this is the final flush.
    if (!final) {
      const p = body.match(PARTIAL_RE);
      if (p) { this.buf = p[0]; body = body.slice(0, body.length - p[0].length); }
      else this.buf = '';
    } else this.buf = '';

    let speech = '', display = '', prose = '', last = 0;
    TAG_RE.lastIndex = 0;
    for (let m = TAG_RE.exec(body); m; m = TAG_RE.exec(body)) {
      this.emit(body.slice(last, m.index), (s, d, p) => { speech += s; display += d; prose += p; });
      const tag = resolveTag(m[1]);
      if (tag) { this.pending = tag; if (this.show) display += m[0]; }
      else log.debug(`dropping unknown emotion tag ${m[0]}`);
      last = m.index + m[0].length;
    }
    this.emit(body.slice(last), (s, d, p) => { speech += s; display += d; prose += p; });
    return { speech, display, prose };
  }

  /** Emit a prose span, flushing any pending tag onto its FRONT (only once real words appear). */
  private emit(text: string, sink: (s: string, d: string, p: string) => void): void {
    if (!text) return;
    let speech = text;
    // Hug the tag onto the first span carrying actual WORDS (not bare punctuation/space) — a tag-only or
    // tag+punctuation transcript synthesizes no audio and would stall the TTS drain.
    if (this.pending && /[\p{L}\p{N}]/u.test(text)) { speech = cartesiaTag(this.pending) + text; this.pending = null; }
    sink(speech, text, text);
  }
}
